import { useState, useEffect, useCallback } from "react";
import { Link, Outlet, useMatches } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { fetchAuthMe, loginWithGoogle, type AuthMeResponse } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: { theme: string; size: string; width: number },
          ) => void;
        };
      };
    };
  }
}

const resources = [
  { path: "/logins", label: "Logins" },
  { path: "/rooms", label: "Rooms" },
  { path: "/hands", label: "Hands" },
  { path: "/players", label: "Players" },
  { path: "/room-events", label: "Room Events" },
  { path: "/player-relationships", label: "Player Relationships" },
  { path: "/chat-messages", label: "Chat Messages" },
  { path: "/user-stats", label: "User Stats" },
  { path: "/admin-logins", label: "Admin Logins" },
  { path: "/player-requests", label: "Player Requests" },
];

function GoogleLoginButton() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        setError(null);
        const user = await loginWithGoogle(response.credential);
        queryClient.setQueryData(queryKeys.auth.me, user);
      } catch (e) {
        setError("Login failed. Please try again.");
      }
    },
    [queryClient],
  );

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    function tryInit() {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
        });
        const el = document.getElementById("google-login-button");
        if (el) {
          window.google.accounts.id.renderButton(el, {
            theme: "filled_black",
            size: "large",
            width: 300,
          });
        }
        return true;
      }
      return false;
    }

    if (!tryInit()) {
      const interval = setInterval(() => {
        if (tryInit()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleCredentialResponse]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div id="google-login-button" />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

function AdminLayout() {
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.pathname ?? "";

  return (
    <div className="dark flex min-h-screen bg-background text-foreground">
      <aside className="w-56 shrink-0 border-r border-zinc-800 bg-zinc-950 p-4">
        <Link to="/" className="block text-lg font-bold mb-6 text-zinc-100 hover:text-white">
          Admin
        </Link>
        <nav className="space-y-1">
          {resources.map((r) => (
            <Link
              key={r.path}
              to={r.path as string}
              className={cn(
                "block rounded px-3 py-1.5 text-sm transition-colors",
                currentPath.startsWith(r.path)
                  ? "bg-zinc-800 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50",
              )}
            >
              {r.label}
            </Link>
          ))}
        </nav>
      </aside>
      <main className="flex-1 p-6 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

export function RootLayout() {
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: fetchAuthMe,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground">
        <p className="text-zinc-400">Loading...</p>
      </div>
    );
  }

  // Not authenticated
  if (error || !user) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-center space-y-6">
          <h1 className="text-2xl font-bold text-zinc-100">moonpoker.live admin</h1>
          <p className="text-zinc-400">Sign in with Google to continue</p>
          <GoogleLoginButton />
        </div>
      </div>
    );
  }

  // Authenticated but not admin
  if (!user.is_admin) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-zinc-100">Not Authorized</h1>
          <p className="text-zinc-400">
            You are signed in as <span className="text-zinc-200">{user.email}</span>, but this
            account does not have admin access.
          </p>
        </div>
      </div>
    );
  }

  // Admin — render the app
  return <AdminLayout />;
}
